import { DT_STAGES } from "../constants/appConfig";

export function emptyCheckpoint() {
  return { fromDay: 1, toDay: 5, stageKey: "empathize", goal: "" };
}

export function parseProductStagePlan(raw) {
  let arr = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      arr = [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((r) => ({
      fromDay: Math.max(1, Number(r?.fromDay) || 1),
      toDay: Math.max(1, Number(r?.toDay) || 1),
      stageKey: String(r?.stageKey || r?.stage || "empathize").toLowerCase(),
      goal: String(r?.goal || r?.note || "").trim().slice(0, 220),
    }))
    .map((r) => (r.toDay < r.fromDay ? { ...r, toDay: r.fromDay } : r))
    .filter((r) => r.stageKey);
}

export function checkpointForDay(planDay1Based, plan) {
  const day = Math.max(1, Number(planDay1Based) || 1);
  return parseProductStagePlan(plan).find((r) => day >= r.fromDay && day <= r.toDay) || null;
}

function sliceStages(startKey, endKey) {
  const keys = DT_STAGES.map((s) => s.key);
  let a = keys.indexOf(startKey);
  let b = keys.indexOf(endKey);
  if (a < 0) a = 0;
  if (b < 0) b = keys.length - 1;
  if (b < a) [a, b] = [b, a];
  return DT_STAGES.slice(a, b + 1);
}

function packStagesAcrossDays(days, stages) {
  const n = stages.length;
  if (!n || days < 1) return [];
  const base = Math.max(1, Math.floor(days / n));
  let extra = Math.max(0, days - base * n);
  let cursor = 1;
  const out = [];
  stages.forEach((s) => {
    if (cursor > days) return;
    const len = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
    const fromDay = cursor;
    const toDay = Math.min(days, cursor + len - 1);
    out.push({ fromDay, toDay, stageKey: s.key, goal: "" });
    cursor = toDay + 1;
  });
  if (out.length && out[out.length - 1].toDay < days) {
    out[out.length - 1].toDay = days;
  }
  return out;
}

export function spreadProductStages(numDays, startKey = "empathize") {
  const days = Math.max(1, Number(numDays) || 10);
  const start = Math.max(0, DT_STAGES.findIndex((s) => s.key === startKey));
  return packStagesAcrossDays(days, DT_STAGES.slice(start));
}

export function spreadToEndStage(numDays, startKey = "empathize", endKey = "implement") {
  const days = Math.max(1, Number(numDays) || 10);
  return packStagesAcrossDays(days, sliceStages(startKey, endKey));
}

export function formatProductStageBlock(plan, numDays) {
  const rows = parseProductStagePlan(plan);
  if (!rows.length) return "";
  const lines = [
    `DT PLAYBOOK MAP — ${numDays || rows[rows.length - 1]?.toDay || "?"} working days.`,
    "Map Project Build (and related DT work) to THIS stage on these days. Use Stage / Step / What to Learn / Real Project Task / Discussion Prompt.",
    "Do not jump ahead of the mapped stage.",
  ];
  rows.forEach((r) => {
    const stage = DT_STAGES.find((s) => s.key === r.stageKey);
    const label = stage?.label || r.stageKey;
    const desc = stage?.desc || "";
    lines.push(`Days ${r.fromDay}–${r.toDay}: Stage = ${label}${r.goal ? ` | Goal: ${r.goal}` : ""}`);
    if (desc) lines.push(`  Playbook: ${desc}`);
  });
  return lines.join("\n");
}

export function dtPlaybookMapPayload(plan, numDays, startKey = "") {
  const rows = parseProductStagePlan(plan);
  // The API resolves the playbook map by working-day number. Keep the editable
  // range format for display, and also send the day-indexed format it consumes.
  const days = {};
  rows.forEach((range) => {
    for (let day = range.fromDay; day <= range.toDay; day += 1) {
      const stage = DT_STAGES.find((s) => s.key === range.stageKey);
      days[String(day)] = {
        stageKey: range.stageKey,
        stage: stage?.label || range.stageKey,
        stageLabel: stage?.label || range.stageKey,
        intoDay: day - range.fromDay,
        goal: range.goal || stage?.desc || "",
      };
    }
  });
  return {
    startStage: startKey || rows[0]?.stageKey || "empathize",
    numDays: Number(numDays) || rows[rows.length - 1]?.toDay || 0,
    days,
    ranges: rows.map((r) => {
      const stage = DT_STAGES.find((s) => s.key === r.stageKey);
      return {
        fromDay: r.fromDay,
        toDay: r.toDay,
        stageKey: r.stageKey,
        stageLabel: stage?.label || r.stageKey,
        goal: r.goal || stage?.desc || "",
      };
    }),
  };
}
